import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import { motion } from "framer-motion";
import { ChevronLeft, Package, Truck } from "lucide-react";
import api from "../lib/api";
import { useRikshawStream } from "../hooks/useRikshawStream";
import { LiveStatusDot } from "../components/LiveStatusDot";
import ShareTrackingButton from "../components/ShareTrackingButton";

const icon = new L.Icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

export default function OrderTrack() {
  const { order_no } = useParams();
  const [order, setOrder] = useState(null);
  const [rikshaw, setRikshaw] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/orders/${order_no}`);
      setOrder(data);
      if (data.assigned_rikshaw_id) {
        const r = await api.get(`/rikshaws`);
        const found = (r.data.rikshaws || []).find((x) => x.id === data.assigned_rikshaw_id);
        setRikshaw(found || null);
      }
    } catch (e) {
      setError(e?.response?.status === 404 ? "Order not found." : "Could not load.");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order_no]);

  useRikshawStream((evt) => {
    setRikshaw((curr) => {
      if (!curr || evt.rikshaw_id !== curr.id) return curr;
      return { ...curr, lat: evt.lat, lng: evt.lng, status: evt.status, last_ping_at: evt.last_ping_at };
    });
  });

  if (!localStorage.getItem("jow_token")) return <Navigate to="/login" replace />;

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="track-error">
        <h1 className="font-display text-3xl text-jow-navy">{error}</h1>
        <Link to="/account/orders" className="btn-secondary-jow mt-6 inline-flex">Back to orders</Link>
      </div>
    );
  }
  if (!order) return null;

  const center = rikshaw ? [rikshaw.lat || rikshaw.current_lat, rikshaw.lng || rikshaw.current_lng] : [22.97, 78.65];

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16" data-testid={`track-${order.order_no}`}>
      <Link to={`/account/orders/${order.order_no}`} className="label-overline text-jow-pink inline-flex items-center gap-1">
        <ChevronLeft size={14} /> Order details
      </Link>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-5">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="label-overline text-jow-gold">Live tracking</span>
            <h1 className="font-display text-4xl md:text-5xl text-jow-navy mt-2">{order.order_no}</h1>
            <p className="text-sm text-jow-navy/55 mt-1">
              Status: <strong>{order.status.replace("_", " ")}</strong>
            </p>
          </div>
          <ShareTrackingButton orderNo={order.order_no} />
        </div>
      </motion.div>

      <div className="grid lg:grid-cols-12 gap-8 mt-8 items-start">
        <div className="lg:col-span-8 border border-jow-gold/40 bg-white" style={{ height: "460px" }} data-testid="track-map">
          <MapContainer center={center} zoom={rikshaw ? 12 : 5} scrollWheelZoom={true} style={{ height: "100%", width: "100%" }}>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {rikshaw && (
              <Marker position={[rikshaw.lat || rikshaw.current_lat, rikshaw.lng || rikshaw.current_lng]} icon={icon}>
                <Popup>
                  <strong>{rikshaw.code}</strong>
                  <br />
                  {rikshaw.driver_name}
                </Popup>
              </Marker>
            )}
          </MapContainer>
        </div>

        <aside className="lg:col-span-4 space-y-5">
          <div className="bg-white border border-jow-navy/10 p-5" data-testid="track-rikshaw">
            <p className="label-overline text-jow-navy/55 mb-2">Rikshaw</p>
            {rikshaw ? (
              <>
                <p className="font-display text-xl text-jow-navy">{rikshaw.code}</p>
                <p className="text-sm text-jow-navy/65 mt-1">{rikshaw.driver_name}</p>
                <div className="mt-3">
                  <LiveStatusDot lastPingAt={rikshaw.last_ping_at} testid="track-live-dot" />
                </div>
              </>
            ) : (
              <p className="text-sm text-jow-navy/55">
                <Truck size={14} className="inline text-jow-gold mr-1" />
                Not yet on a rikshaw.
              </p>
            )}
          </div>
          <div className="bg-white border border-jow-navy/10 p-5">
            <p className="label-overline text-jow-navy/55 mb-3">Timeline</p>
            <ol className="space-y-3" data-testid="track-timeline">
              {order.timeline.map((t, i) => (
                <li key={i} className="flex gap-3 text-sm">
                  <Package size={13} className="text-jow-gold mt-1" />
                  <div>
                    <p className="text-jow-navy capitalize">{t.status.replaceAll("_", " ")}</p>
                    <p className="text-[10px] text-jow-navy/50">{new Date(t.at).toLocaleString()}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </aside>
      </div>
    </div>
  );
}
